import React, { useState } from 'react';
import { FiMail, FiAlertCircle } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { toast } from '../Toast';
import { BillingDetails, useClient } from './clientSettingsData';
import SettingsModal from './SettingsModal';

interface Props {
    details: BillingDetails;
    onClose: () => void;
    // preview: updates the Billing page only
    onSave: (details: BillingDetails) => void;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EditBillingModal: React.FC<Props> = ({ details, onClose, onSave }) => {
    const { name } = useClient();
    const [form, setForm] = useState<BillingDetails>(details);
    const set = <K extends keyof BillingDetails>(field: K, value: BillingDetails[K]) => setForm(f => ({ ...f, [field]: value }));

    const canSave = EMAIL.test(form.email.trim());

    const save = () => {
        onSave(form);
        toast.info('Preview only: this isn’t connected yet, so the billing details were not saved');
    };

    const input = (field: keyof BillingDetails, label: string, extra?: { optional?: boolean; placeholder?: string; type?: string }) =>
        <label className="field">
            <span className="label">{label}{extra?.optional && <em>(optional)</em>}</span>
            <input type={extra?.type ?? 'text'} value={form[field] as string} placeholder={extra?.placeholder} onChange={e => set(field, e.target.value)} />
        </label>;

    return <SettingsModal icon={<FiMail />} title="Edit Billing Details" sub="What appears on the invoice and where it's emailed" onClose={onClose}
        footerNote="Only the account owner can change billing details."
        footer={<button type="button" className="btn-primary" onClick={save} disabled={!canSave}>Save Details</button>}>
        <div className="field">
            <span className="label">Billed to (legal name) <em>· managed by Investi</em></span>
            <div className="value-box locked"><span>{name}</span><ShieldCheckIcon /></div>
        </div>
        <div className="fields">
            {input('abn', 'ABN')}
            {input('po', 'Purchase order number', { optional: true, placeholder: 'e.g. PO‑2026‑118' })}
        </div>
        <div className="fields">
            {input('contact', 'Billing contact')}
            {input('email', 'Billing email', { type: 'email' })}
        </div>
        <div className="fields">
            {input('address', 'Billing address')}
            {input('suburb', 'Suburb')}
        </div>
        <div className="fields">
            {input('state', 'State')}
            {input('postcode', 'Postcode')}
        </div>
        <div className="modal-note"><FiAlertCircle /> The legal name comes from Client settings › Company and can only be changed by Investi. Changes here apply to the next invoice, not to invoices already issued.</div>
    </SettingsModal>;
};

export default EditBillingModal;
